import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Mail, Lock, User, Eye, EyeOff, Check, X } from 'lucide-react';

import { API } from '@/lib/api';

function getPasswordStrength(pw) {
  let score = 0;
  if (pw.length >= 6) score++;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return score;
}

const strengthLabels = { de: ['', 'Sehr schwach', 'Schwach', 'Mittel', 'Stark', 'Sehr stark'], en: ['', 'Very weak', 'Weak', 'Medium', 'Strong', 'Very strong'] };
const strengthColors = ['', 'bg-red-400', 'bg-orange-400', 'bg-yellow-400', 'bg-green-400', 'bg-green-500'];

export default function AuthPage() {
  const { t, language } = useLanguage();
  const { login, register, user } = useAuth();
  const nav = useNavigate();
  const de = language === 'de';
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get('mode') === 'register' ? 'register' : 'login');
  const [form, setForm] = useState({ email: '', password: '', confirmPassword: '', name: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetSent, setResetSent] = useState(false);

  useEffect(() => {
    if (user) nav(user.onboarding_completed ? '/dashboard' : '/onboarding', { replace: true });
  }, [user, nav]);

  const strength = getPasswordStrength(form.password);
  const passwordsMatch = form.password === form.confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'register') {
      if (form.password.length < 6) return toast.error(de ? 'Passwort muss mind. 6 Zeichen haben' : 'Password must be at least 6 characters');
      if (!passwordsMatch) return toast.error(de ? 'Passwörter stimmen nicht überein' : 'Passwords do not match');
      if (!form.name.trim()) return toast.error(de ? 'Name ist erforderlich' : 'Name is required');
    }
    setLoading(true);
    try {
      const u = mode === 'register'
        ? await register(form.email, form.password, form.name)
        : await login(form.email, form.password);
      if (mode === 'register') toast.success(de ? 'Willkommen bei belosophie!' : 'Welcome to belosophie!');
      nav(u.onboarding_completed ? '/dashboard' : '/onboarding');
    } catch (err) {
      console.error('Auth error:', err);
      const msg = err.response?.data?.detail || err.message || 'Verbindungsfehler';
      toast.error(msg === 'Email already registered' ? (de ? 'E-Mail bereits registriert' : msg) :
        msg === 'Invalid credentials' ? (de ? 'Ungültige Anmeldedaten' : msg) :
        msg === 'Network Error' ? (de ? 'Keine Verbindung zum Server' : 'No connection to server') : msg);
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: resetEmail });
      setResetSent(true);
      if (res.data.reset_token) setResetToken(res.data.reset_token);
      toast.success(de ? 'Reset-Code gesendet!' : 'Reset code sent!');
    } catch { toast.error(de ? 'Fehler' : 'Error'); }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) return toast.error(de ? 'Passwort muss mind. 6 Zeichen haben' : 'Password must be at least 6 characters');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetToken, new_password: newPassword });
      toast.success(de ? 'Passwort zurückgesetzt!' : 'Password reset!');
      setMode('login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
    setLoading(false);
  };

  const handleGoogle = () => {
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center p-6" data-testid="auth-page">
      <div className="w-full max-w-md">
        <button onClick={() => nav('/')} className="flex items-center gap-2 text-foreground/60 hover:text-foreground mb-8 font-body text-sm transition-colors" data-testid="auth-back-btn">
          <ArrowLeft size={16} /> <span className="font-display text-xl font-semibold"><span className="text-terracotta">bel</span>osophie</span>
        </button>

        <Card className="bg-white border border-border/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.05)] rounded-2xl p-8" data-testid="auth-card">

          {/* === FORGOT PASSWORD MODE === */}
          {mode === 'forgot' && (
            <>
              <h1 className="font-display text-3xl font-medium mb-2" data-testid="auth-title">
                {de ? 'Passwort vergessen' : 'Forgot Password'}
              </h1>
              <p className="font-body text-foreground/50 mb-6 text-sm">
                {de ? 'Gib deine E-Mail ein und wir senden dir einen Reset-Code.' : 'Enter your email and we\'ll send you a reset code.'}
              </p>
              {!resetSent ? (
                <form onSubmit={handleForgotPassword} className="space-y-5">
                  <div>
                    <Label className="font-body text-sm text-foreground/70 mb-1.5">{t('auth.email')}</Label>
                    <div className="relative">
                      <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/30" />
                      <Input type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)} placeholder="anna@example.com" className="pl-10 rounded-xl border-border/80 py-5 font-body" data-testid="reset-email-input" required />
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full rounded-full bg-terracotta hover:bg-terracotta-dark text-white py-5 text-base font-medium shadow-lg shadow-terracotta/20" data-testid="reset-send-btn">
                    {loading ? '...' : de ? 'Reset-Code senden' : 'Send Reset Code'}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-5">
                  <div>
                    <Label className="font-body text-sm text-foreground/70 mb-1.5">{de ? 'Reset-Code' : 'Reset Code'}</Label>
                    <Input value={resetToken} onChange={e => setResetToken(e.target.value)} placeholder="Code eingeben..." className="rounded-xl border-border/80 py-5 font-body font-mono tracking-widest" data-testid="reset-token-input" required />
                  </div>
                  <div>
                    <Label className="font-body text-sm text-foreground/70 mb-1.5">{de ? 'Neues Passwort' : 'New Password'}</Label>
                    <div className="relative">
                      <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/30" />
                      <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="********" className="pl-10 rounded-xl border-border/80 py-5 font-body" data-testid="reset-new-password" required />
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full rounded-full bg-terracotta hover:bg-terracotta-dark text-white py-5 text-base font-medium shadow-lg shadow-terracotta/20" data-testid="reset-confirm-btn">
                    {loading ? '...' : de ? 'Passwort zurücksetzen' : 'Reset Password'}
                  </Button>
                </form>
              )}
              <p className="text-center mt-6 font-body text-sm text-foreground/50">
                <button onClick={() => { setMode('login'); setResetSent(false); }} className="text-terracotta font-medium hover:underline" data-testid="back-to-login">
                  {de ? 'Zurück zur Anmeldung' : 'Back to login'}
                </button>
              </p>
            </>
          )}

          {/* === LOGIN / REGISTER MODE === */}
          {mode !== 'forgot' && (
            <>
              <h1 className="font-display text-3xl font-medium mb-2" data-testid="auth-title">
                {mode === 'register' ? t('auth.register_title') : t('auth.login_title')}
              </h1>
              <p className="font-body text-foreground/50 mb-6 text-sm">
                {mode === 'register'
                  ? (de ? 'Erstelle dein kostenloses Konto' : 'Create your free account')
                  : (de ? 'Schön, dass du wieder da bist' : 'Welcome back')}
              </p>

              <Button variant="outline" onClick={handleGoogle} className="w-full rounded-full py-5 mb-5 border-border/80 hover:bg-warmgray/50 font-body text-base" data-testid="google-login-btn">
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                {t('auth.google_btn')}
              </Button>

              <div className="flex items-center gap-4 mb-5">
                <Separator className="flex-1" />
                <span className="font-body text-xs text-foreground/40 uppercase tracking-wider">{t('auth.or')}</span>
                <Separator className="flex-1" />
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {mode === 'register' && (
                  <div>
                    <Label className="font-body text-sm text-foreground/70 mb-1.5">{t('auth.name')}</Label>
                    <div className="relative">
                      <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/30" />
                      <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Belinda Muster" className="pl-10 rounded-xl border-border/80 py-5 font-body" data-testid="register-name-input" required />
                    </div>
                  </div>
                )}
                <div>
                  <Label className="font-body text-sm text-foreground/70 mb-1.5">{t('auth.email')}</Label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/30" />
                    <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="anna@example.com" className="pl-10 rounded-xl border-border/80 py-5 font-body" data-testid="auth-email-input" required />
                  </div>
                </div>
                <div>
                  <Label className="font-body text-sm text-foreground/70 mb-1.5">{t('auth.password')}</Label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/30" />
                    <Input type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="********" className="pl-10 pr-10 rounded-xl border-border/80 py-5 font-body" data-testid="auth-password-input" required />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/30 hover:text-foreground/60" data-testid="toggle-password-visibility">
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {mode === 'register' && form.password && (
                    <div className="mt-2 space-y-1.5">
                      <div className="flex items-center gap-2">
                        <Progress value={strength * 20} className={`h-1.5 flex-1 bg-warmgray [&>div]:${strengthColors[strength]}`} data-testid="password-strength-bar" />
                        <span className="font-body text-[10px] text-foreground/50 w-20 text-right">{(de ? strengthLabels.de : strengthLabels.en)[strength]}</span>
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1">
                        {[
                          { check: form.password.length >= 6, label: de ? '6+ Zeichen' : '6+ chars' },
                          { check: /[A-Z]/.test(form.password), label: de ? 'Grossbuchstabe' : 'Uppercase' },
                          { check: /[0-9]/.test(form.password), label: de ? 'Zahl' : 'Number' },
                        ].map((r, i) => (
                          <span key={i} className={`flex items-center gap-1 font-body text-[10px] ${r.check ? 'text-green-600' : 'text-foreground/30'}`}>
                            {r.check ? <Check size={10} /> : <X size={10} />} {r.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {mode === 'register' && (
                  <div>
                    <Label className="font-body text-sm text-foreground/70 mb-1.5">{de ? 'Passwort bestätigen' : 'Confirm Password'}</Label>
                    <div className="relative">
                      <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/30" />
                      <Input type="password" value={form.confirmPassword} onChange={e => setForm({ ...form, confirmPassword: e.target.value })} placeholder="********" className={`pl-10 rounded-xl border-border/80 py-5 font-body ${form.confirmPassword && !passwordsMatch ? 'border-red-300 focus-visible:ring-red-300' : ''}`} data-testid="auth-confirm-password" required />
                      {form.confirmPassword && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {passwordsMatch ? <Check size={18} className="text-green-500" /> : <X size={18} className="text-red-400" />}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <Button type="submit" disabled={loading || (mode === 'register' && (!passwordsMatch || strength < 2))} className="w-full rounded-full bg-terracotta hover:bg-terracotta-dark text-white py-5 text-base font-medium shadow-lg shadow-terracotta/20 hover:scale-[1.02] transition-all" data-testid="auth-submit-btn">
                  {loading ? '...' : mode === 'register' ? t('auth.register_btn') : t('auth.login_btn')}
                </Button>
              </form>

              {mode === 'login' && (
                <p className="text-center mt-4">
                  <button onClick={() => setMode('forgot')} className="font-body text-xs text-foreground/40 hover:text-terracotta transition-colors" data-testid="forgot-password-link">
                    {de ? 'Passwort vergessen?' : 'Forgot password?'}
                  </button>
                </p>
              )}

              <p className="text-center mt-4 font-body text-sm text-foreground/50">
                {mode === 'register' ? (de ? 'Schon ein Konto?' : t('auth.has_account')) : (de ? 'Noch kein Konto?' : t('auth.no_account'))}{' '}
                <button onClick={() => setMode(mode === 'register' ? 'login' : 'register')} className="text-terracotta font-medium hover:underline" data-testid="auth-toggle-mode">
                  {mode === 'register' ? t('auth.login_btn') : t('auth.register_btn')}
                </button>
              </p>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
